/**
 * CalendarView — shows inventory submissions on a calendar with bulk date editing.
 */
import { useMemo } from "react";
import { format } from "date-fns";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Calendar } from "../ui/calendar";
import { CalendarBlank, Pencil, Check } from "@phosphor-icons/react";

export function CalendarView({
  calendarMonth, setCalendarMonth,
  calendarLocation, setCalendarLocation,
  calendarLoading, submissions, locations,
  selectedSubmission, setSelectedSubmission,
  newSubmissionDate, setNewSubmissionDate,
  updatingSubmission, onUpdateSubmissionDate
}) {
  const submissionsByDate = useMemo(() => {
    const map = {};
    for (const sub of submissions) {
      if (!map[sub.date]) map[sub.date] = [];
      map[sub.date].push(sub);
    }
    return map;
  }, [submissions]);

  const submissionDates = useMemo(() => {
    return Object.keys(submissionsByDate).map(d => {
      const [y, m, day] = d.split('-').map(Number);
      return new Date(y, m - 1, day);
    });
  }, [submissionsByDate]);

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <div className="w-full sm:w-44">
          <Label className="text-sm text-muted-foreground mb-1 block">Location</Label>
          <Select value={calendarLocation} onValueChange={setCalendarLocation}>
            <SelectTrigger data-testid="calendar-location-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map(loc => (
                <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <Card className="border rounded-xl flex-shrink-0">
          <CardContent className="p-4">
            <Calendar
              mode="single"
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              modifiers={{ hasSubmission: submissionDates }}
              modifiersClassNames={{ hasSubmission: "!bg-primary !text-primary-foreground !font-bold !rounded-full" }}
              onDayClick={(day) => {
                const dateStr = format(day, "yyyy-MM-dd");
                const subs = submissionsByDate[dateStr];
                if (subs && subs.length > 0) {
                  setSelectedSubmission(subs[0]);
                  setNewSubmissionDate(subs[0].date);
                } else {
                  setSelectedSubmission(null);
                }
              }}
              className="rounded-md"
              data-testid="inventory-calendar"
            />
            {calendarLoading && (
              <div className="flex justify-center py-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex-1 space-y-3">
          <h3 className="font-heading text-lg tracking-wide">
            SUBMISSIONS — {format(calendarMonth, "MMMM yyyy").toUpperCase()}
          </h3>
          {submissions.length === 0 ? (
            <Card className="border rounded-xl">
              <CardContent className="py-12 text-center">
                <CalendarBlank size={40} weight="duotone" className="mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No inventory submissions this month</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {submissions.map((sub) => (
                <Card
                  key={sub.submission_key}
                  data-testid={`submission-${sub.submission_key}`}
                  className={`border rounded-xl cursor-pointer transition-all hover:shadow-md ${
                    selectedSubmission?.submission_key === sub.submission_key ? 'ring-2 ring-primary border-primary' : ''
                  }`}
                  onClick={() => { setSelectedSubmission(sub); setNewSubmissionDate(sub.date); }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{sub.date}</span>
                          <Badge variant="outline" className="text-[10px]">{sub.location_name}</Badge>
                          {sub.product_count > 0 && <Badge variant="secondary" className="text-[10px]">{sub.product_count} products</Badge>}
                          {sub.prep_count > 0 && <Badge variant="secondary" className="text-[10px]">{sub.prep_count} prep</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          By {sub.recorded_by_name} &middot; {sub.total_items} items
                          {sub.total_value > 0 && <> &middot; ${sub.total_value.toFixed(2)}</>}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setSelectedSubmission(sub); setNewSubmissionDate(sub.date); }}>
                        <Pencil size={14} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Submission Date Dialog */}
      <Dialog open={!!selectedSubmission} onOpenChange={(open) => !open && setSelectedSubmission(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">EDIT SUBMISSION DATE</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1">
              <p className="text-sm font-medium">{selectedSubmission?.location_name}</p>
              <p className="text-xs text-muted-foreground">
                {selectedSubmission?.total_items} items by {selectedSubmission?.recorded_by_name}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {selectedSubmission?.product_count > 0 && <Badge variant="secondary" className="text-[10px]">{selectedSubmission.product_count} products</Badge>}
                {selectedSubmission?.prep_count > 0 && <Badge variant="secondary" className="text-[10px]">{selectedSubmission.prep_count} prep items</Badge>}
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Current Date</Label>
              <p className="font-medium">{selectedSubmission?.date}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">New Date</Label>
              <Input type="date" value={newSubmissionDate} onChange={(e) => setNewSubmissionDate(e.target.value)} data-testid="submission-new-date" />
            </div>
            {newSubmissionDate && newSubmissionDate !== selectedSubmission?.date && (
              <p className="text-xs text-amber-600 font-medium">
                All {selectedSubmission?.total_items} records will be moved from {selectedSubmission?.date} to {newSubmissionDate}
              </p>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSelectedSubmission(null)}>Cancel</Button>
            <Button
              onClick={onUpdateSubmissionDate}
              disabled={updatingSubmission || !newSubmissionDate || newSubmissionDate === selectedSubmission?.date}
              className="bg-primary"
              data-testid="save-submission-date-btn"
            >
              <Check size={16} className="mr-2" />
              {updatingSubmission ? "Updating..." : "Update Date"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
